// =============================================================================
// src/utils/billDates.js
// One rule for every report screen:
//   Completed bill -> counted on the day it was COMPLETED (completedAt)
//   Draft / cancelled bill -> shown on the day it was STARTED (createdAt)
// =============================================================================

const isValid = (d) => d instanceof Date && !Number.isNaN(d.getTime());
const toDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return isValid(d) ? d : null;
};

/** "YYYY-MM-DD" in the user's own timezone (IST stays IST) */
export const toLocalYMD = (value) => {
    const d = value instanceof Date ? value : toDate(value);
    if (!d || !isValid(d)) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Old bills store `date` as a date-only value (00:00 or 12:00 UTC) which shows a fake time
 * in India. Take the DAY from `date` and the real TIME from `createdAt`.
 */
const dayWithRealTime = (dateValue, timeValue) => {
    const day = toDate(dateValue);
    const time = toDate(timeValue);
    if (!day) return time;
    const isDateOnly = day.getUTCMinutes() === 0 && day.getUTCSeconds() === 0 && day.getUTCMilliseconds() === 0
        && (day.getUTCHours() === 0 || day.getUTCHours() === 12);
    if (!isDateOnly || !time) return day;
    return new Date(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), time.getHours(), time.getMinutes(), time.getSeconds());
};

export const isCompletedBill = (inv) => String(inv?.status || '').toLowerCase() === 'completed';

/** When the sale happened. null for bills that are not completed. */
export const getCompletedAt = (inv) => {
    if (!isCompletedBill(inv)) return null;
    return toDate(inv.completedAt) || dayWithRealTime(inv.date, inv.createdAt) || toDate(inv.updatedAt);
};

export const getCreatedAt = (inv) => toDate(inv?.createdAt) || dayWithRealTime(inv?.date, null);
export const getUpdatedAt = (inv) => toDate(inv?.updatedAt);

/** The date a bill belongs to in every report */
export const getBillDate = (inv) => getCompletedAt(inv) || getCreatedAt(inv) || new Date(NaN);

/** Inclusive "YYYY-MM-DD" range check on the bill date */
export const isBillInRange = (inv, startDate, endDate) => {
    if (!startDate || !endDate) return true;
    const day = toLocalYMD(getBillDate(inv));
    return Boolean(day) && day >= startDate && day <= endDate;
};

export const formatDate = (d) => (d && isValid(d)
    ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(d)
    : '—');

export const formatTime = (d) => (d && isValid(d)
    ? new Intl.DateTimeFormat('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).format(d)
    : '');

/** True when two moments are more than a minute apart (hide "Updated" when it equals "Completed") */
export const differsFrom = (a, b) => Boolean(a && b) && Math.abs(a.getTime() - b.getTime()) > 60 * 1000;